import type { StringifyOwnKeys } from "./stringifyOwnKeys";

/**
 * Strongly-typed `Object.keys` for a precise instantiated type
 * (e.g. an `IFoo.build` from `@faire/web-api`).
 *
 * Numeric key *literals* are stringified the way `Object.keys` does at runtime (`0` → `"0"`).
 * `{ [x: string]: V }`'s extra `| number` is dropped (TS index signature quirk).
 * Generic `T extends string` keys stay `T` so they still index.
 * A lone wide `number` (`Record<number, V>`) becomes `${number}`.
 * @remarks
 * **This should never be used on anything that will be extended.** \
 * The type constraint will no longer be true,
 * since the keys would be a _superset_ of the given type.
 */
export const protoKeys = <T extends object>(
  proto: T
): Array<StringifyOwnKeys<keyof T>> => {
  return Object.keys(proto) as Array<StringifyOwnKeys<keyof T>>;
};

/**
 * Strongly-typed `Object.values` for a precise instantiated type
 * (e.g. an `IFoo.build` from `@faire/web-api`).
 *
 * Stock `Object.values` is `any[]` on interfaces (no implicit index signature).
 * This wrapper is `T[keyof T][]`, which includes `undefined` when fields do.
 * @remarks
 * **This should never be used on anything that will be extended.** \
 * The type constraint will no longer be true,
 * since the values would be a _superset_ of the given type.
 */
export const protoValues = <T extends object>(proto: T): Array<T[keyof T]> => {
  return Object.values(proto) as Array<T[keyof T]>;
};

/**
 * Strongly-typed `Object.entries` for a precise instantiated type
 * (e.g. an `IFoo.build` from `@faire/web-api`).
 *
 * Contains the same key-stringifying type behavior as {@link protoKeys}.
 * @remarks
 * **This should never be used on anything that will be extended.** \
 * The type constraint will no longer be true,
 * since the entries would be a _superset_ of the given type.
 */
export const protoEntries = <T extends object>(
  proto: T
): Array<[StringifyOwnKeys<keyof T>, T[keyof T]]> => {
  return Object.entries(proto) as Array<
    [StringifyOwnKeys<keyof T>, T[keyof T]]
  >;
};

export const protoAssign = <T extends object, S extends object>(
  target: T,
  ...sources: S[]
): T & S => Object.assign(target, ...sources);

/**
 * Return the given value of `indexable` at the given key with the type
 * `unknown`.
 *
 * When the key isn't statically guaranteed to be part
 * of the object's schema, the return value could be anything, as object
 * values can have additional key/value pairs and still satisfy the type
 * signature of a smaller schema. For example, `{a: 1, b: 'hi'}` is a
 * valid instance of `{a: number}`, so indexing it with an arbitrary index
 * could return a string, even though string is never even mentioned in
 * the type.
 */
export const getUnknownProperty = (
  indexable: object,
  key: number | string | symbol
): unknown => {
  const asRecord: Partial<Record<number | string | symbol, unknown>> =
    indexable;
  return asRecord[key];
};

/**
 * Return the given value of `indexable` at the given key, if it is a string,
 * and otherwise return `undefined`.
 */
export const getStringProperty = (
  indexable: object,
  key: number | string | symbol
): string | undefined => {
  const valueUnknown = getUnknownProperty(indexable, key);
  return typeof valueUnknown === "string" ? valueUnknown : undefined;
};

export const keyBy = <T extends number | string | symbol, S>(
  array: T[],
  iteratee: (elem: T, index?: number) => S
): { [K in T]: S } =>
  Object.assign(
    {},
    ...array.map((elem, index) => ({ [elem]: iteratee(elem, index) }))
  );
